/**
 * Delivery-vendor verification (ported from cpanel
 * `features/merchants/model/merchantForm.ts` — logic 1:1).
 *
 * The delivery tab stays in the "processing" state until the by-external-id
 * vendor exists AND its name/INN/legal-form match the merchant. A mismatch is
 * an explicit error, never a silent substitution.
 */
import * as z from 'zod';
import { LEGAL_FORM_LABELS } from './merchant-labels';
import { merchantErrorMessage } from './merchant-mappers';

export const deliveryVendorSchema = z
  .object({
    id: z.union([z.string(), z.number()]).nullish(),
    name: z.string().nullish(),
    inn: z.string().nullish(),
    legal_form: z.string().nullish(),
    is_business: z.boolean().nullish(),
    is_corporate: z.union([z.boolean(), z.number()]).nullish(),
    user_form: z.unknown().optional(),
    // Detail fields consumed by the delivery-tab vendor form (all nullish —
    // the backend returns them as null when unset, and the verification read
    // only needs the identity fields above: id/name/inn/legal_form plus legacy
    // business flags carried for the delivery form.
    logo: z.string().nullish(),
    business_logo: z.string().nullish(),
    profile_photo: z.string().nullish(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    phone: z.string().nullish(),
    business_number: z.string().nullish(),
    business_name: z.string().nullish(),
    official_business_name: z.string().nullish(),
    preferred_lang: z.string().nullish(),
    email: z.string().nullish(),
    business_desc: z.string().nullish(),
    address: z.string().nullish(),
    minimal_balance: z.union([z.string(), z.number()]).nullish(),
    search_time: z.union([z.string(), z.number()]).nullish(),
    contract_number: z.string().nullish(),
    contract_start_date: z.string().nullish(),
    // Webhook settings — the by-external-id row carries them flat; older
    // rows may nest them as `callback: {url, token}` (normalized to the
    // flat pair on read in `merchantApi.deliveryVendor`).
    callback_url: z.string().nullish(),
    callback_token: z.string().nullish(),
    callback: z
      .object({
        url: z.string().nullish(),
        token: z.string().nullish(),
      })
      .nullish(),
    enable_balance_restrictions: z.boolean().nullish(),
    door_to_door_required: z.boolean().nullish(),
    allow_multiple_orders: z.boolean().nullish(),
    is_client_full_pay: z.boolean().nullish(),
    is_sanctioned: z.boolean().nullish(),
    is_order_blocked: z.boolean().nullish(),
    sales_manager: z
      .object({ id: z.union([z.string(), z.number()]).nullish() })
      .nullish(),
  })
  .loose()
  .passthrough();
export type DeliveryVendor = z.infer<typeof deliveryVendorSchema>;

function normalizeText(value?: string | null): string {
  return (value ?? '').trim();
}

function normalizeInn(value?: string | null): string {
  return normalizeText(value).replace(/\D/g, '');
}

function normalizeLegalForm(value?: string | null): string {
  return normalizeText(value);
}

export interface MerchantDeliveryComparison {
  matches: boolean;
  mismatches: ('name' | 'inn' | 'legal_form')[];
  waitingForInn: boolean;
}

export function compareMerchantWithDeliveryVendor(
  merchant:
    | {
        name?: string | undefined;
        inn?: string | undefined;
        legal_form?: string | undefined;
      }
    | null
    | undefined,
  vendor: DeliveryVendor | undefined,
): MerchantDeliveryComparison {
  if (!merchant || !vendor)
    return { matches: false, mismatches: [], waitingForInn: false };

  // TEMP: identity checks disabled — only the 404 (missing vendor) error and
  // the poll/retry budget in `use-delivery-integration.ts` still gate the tab.
  void normalizeInn;
  void normalizeLegalForm;
  // const waitingForInn = !normalizeInn(vendor.inn);
  // if (waitingForInn) return { matches: false, mismatches: [], waitingForInn };

  const mismatches: MerchantDeliveryComparison['mismatches'] = [];

  // if (normalizeText(merchant.name) !== normalizeText(vendor.name))
  //   mismatches.push('name');
  // if (normalizeInn(merchant.inn) !== normalizeInn(vendor.inn)) mismatches.push('inn');
  // if (normalizeLegalForm(merchant.legal_form) !== normalizeLegalForm(vendor.legal_form))
  //   mismatches.push('legal_form');

  return { matches: true, mismatches, waitingForInn: false };
}

type MerchantIdentity =
  | {
      name?: string | undefined;
      inn?: string | undefined;
      legal_form?: string | undefined;
    }
  | null
  | undefined;

/** Render a value for the mismatch details — never an empty string. */
function quoteForMismatch(value: string): string {
  const trimmed = value.trim();
  return trimmed === '' ? '—' : `«${trimmed}»`;
}

function merchantLegalLabel(legalForm?: string): string {
  if (typeof legalForm === 'string' && legalForm !== '') {
    const label = LEGAL_FORM_LABELS[legalForm];
    if (label !== undefined) return label;
    return legalForm;
  }
  return '—';
}

function vendorLegalLabel(legalForm?: string | null): string {
  if (typeof legalForm === 'string' && legalForm !== '') {
    const label = LEGAL_FORM_LABELS[legalForm];
    if (label !== undefined) return label;
    return legalForm;
  }
  return '—';
}

/**
 * Per-field verification failures with the actual merchant vs vendor values,
 * so the edit-dialog toast can say WHAT did not match instead of a generic
 * "data does not match".
 */
export function describeDeliveryMismatch(
  merchant: MerchantIdentity,
  vendor: DeliveryVendor | undefined,
  mismatches: MerchantDeliveryComparison['mismatches'],
): string[] {
  if (!merchant || !vendor) return [];
  return mismatches.map((field) => {
    switch (field) {
      case 'name':
        return `название: мерчант ${quoteForMismatch(merchant.name ?? '')} ≠ доставка ${quoteForMismatch(vendor.name ?? '')}`;
      case 'inn':
        return `ИНН/ПИНФЛ: мерчант ${quoteForMismatch(merchant.inn ?? '')} ≠ доставка ${quoteForMismatch(vendor.inn ?? '')}`;
      case 'legal_form':
        return `правовая форма: мерчант ${quoteForMismatch(merchantLegalLabel(merchant.legal_form))} ≠ доставка ${quoteForMismatch(vendorLegalLabel(vendor.legal_form))}`;
    }
  });
}

/** Full mismatch toast text with the per-field details attached. */
export function deliveryMismatchMessage(
  merchant: MerchantIdentity,
  vendor: DeliveryVendor | undefined,
  mismatches: MerchantDeliveryComparison['mismatches'],
): string {
  const fallback = 'Данные доставки не совпадают с данными мерчанта';
  const details = describeDeliveryMismatch(merchant, vendor, mismatches);
  return details.length > 0 ? `${fallback}: ${details.join('; ')}` : fallback;
}

function errorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const direct = (error as { status?: unknown }).status;
  if (typeof direct === 'number') return direct;
  const nested = (error as { response?: { status?: unknown } }).response?.status;
  return typeof nested === 'number' ? nested : undefined;
}

function errorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const direct = (error as { code?: unknown }).code;
  if (typeof direct === 'string' || typeof direct === 'number') return String(direct);
  const nested = (error as { response?: { data?: { error?: { code?: unknown } } } })
    .response?.data?.error?.code;
  if (typeof nested === 'string' || typeof nested === 'number') return String(nested);
  return undefined;
}

/**
 * First Zod validation issues formatted for a toast (`path: message`).
 * The vendor read parses at the boundary, so an HTTP 200 with an unexpected
 * shape surfaces here as a ZodError without a status — that is exactly the
 * "fetch succeeded but the toast still fires" case.
 */
function formatZodIssues(error: unknown, limit = 3): string {
  if (typeof error !== 'object' || error === null) return '';
  const issues = (error as { issues?: unknown }).issues;
  if (!Array.isArray(issues) || issues.length === 0) return '';
  const parts: string[] = [];
  for (const issue of issues.slice(0, limit)) {
    if (typeof issue !== 'object' || issue === null) continue;
    const item = issue as { path?: unknown; message?: unknown };
    const path = Array.isArray(item.path) ? item.path.map(String).join('.') : '';
    const message = typeof item.message === 'string' ? item.message : '';
    if (message === '') continue;
    parts.push(`${path === '' ? '<ответ>' : path}: ${message}`);
  }
  if (parts.length === 0) return '';
  const rest = issues.length - Math.min(issues.length, limit);
  const suffix = rest > 0 ? ` (+${rest} ещё)` : '';
  return `ответ не прошёл проверку: ${parts.join('; ')}${suffix}`;
}

/**
 * Detailed vendor-fetch failure: keeps the `merchantErrorMessage` backend
 * message but prefixes the HTTP status/code context, so a non-404 failure
 * says more than "не удалось получить".
 */
export function deliveryVendorErrorMessage(error: unknown, fallback: string): string {
  const zod = formatZodIssues(error);
  // A Zod message is the same validation summary rephrased — prefer issues.
  const backend = zod === '' ? merchantErrorMessage(error, '') : '';
  const reason = zod !== '' ? zod : backend;
  const status = errorStatus(error);
  const code = errorCode(error);
  const context = [
    status !== undefined ? `HTTP ${status}` : '',
    code !== undefined && code !== '' ? `code ${code}` : '',
  ].filter((part) => part !== '');
  if (context.length > 0 && reason !== '')
    return `${fallback} (${context.join(', ')}): ${reason}`;
  if (context.length > 0) return `${fallback} (${context.join(', ')})`;
  if (reason !== '') return `${fallback}: ${reason}`;
  return fallback;
}
